/**
 * The citation chain as it appears in the audit trail.
 *
 * Replaces the standalone Evidence list. That list showed everything the model
 * touched whether or not the reader cared; this shows what the reader asked
 * for, in the order they asked, so the trail reads as their path through the
 * answer's sources.
 *
 * Cards themselves are unchanged — the same source-typed renderers the
 * evidence list used (`componentRegistry`), so email, Slack, DealCloud, PDF
 * and workbook citations each keep their own presentation, and click-through
 * to the document stays where it already lives: on the card.
 */

import { X } from "lucide-react";
import React, { useCallback, useEffect, useRef } from "react";

import type { EvidenceEvent } from "../../types/evidence";
import { renderEvidenceEvent } from "../registry/componentRegistry";
import type { CitationChainEntry } from "./CitationChainContext";
import "./CitationChain.css";

const FLASH_DURATION_MS = 1200;

export interface CitationChainPanelProps {
	chain: CitationChainEntry[];
	/** Every event on the message — the cards' upstream links resolve here. */
	allEvents: EvidenceEvent[];
	chatId?: string | null;
	focusedKey: string | null;
	onDismiss: (key: string) => void;
}

/** What to show where a card cannot be built. */
const UnresolvedCard: React.FC<{ entry: CitationChainEntry }> = ({ entry }) => (
	<div className="citation-chain-unresolved">
		This citation could not be loaded for this message.
		<span className="citation-chain-unresolved-key">{entry.target.target}</span>
	</div>
);

const CitationChainPanel: React.FC<CitationChainPanelProps> = ({
	chain,
	allEvents,
	chatId,
	focusedKey,
	onDismiss,
}) => {
	const itemRefs = useRef<Map<string, HTMLLIElement>>(new Map());
	// Keyed by entry key -> the revision we last flashed for, so a re-click
	// flashes again while an unrelated re-render does not.
	const flashedRevisions = useRef<Map<string, number>>(new Map());

	const setItemRef = useCallback(
		(key: string) => (node: HTMLLIElement | null) => {
			if (node) itemRefs.current.set(key, node);
			else itemRefs.current.delete(key);
		},
		[],
	);

	const focused = chain.find((entry) => entry.key === focusedKey);
	const focusedRevision = focused?.revision;

	useEffect(() => {
		if (!focusedKey) return;
		const node = itemRefs.current.get(focusedKey);
		if (!node) return;
		const revision = focusedRevision ?? 0;
		if (flashedRevisions.current.get(focusedKey) === revision) return;
		flashedRevisions.current.set(focusedKey, revision);
		node.scrollIntoView({ behavior: "smooth", block: "nearest" });
		node.classList.add("citation-chain-item--flash");
		const timer = window.setTimeout(
			() => node.classList.remove("citation-chain-item--flash"),
			FLASH_DURATION_MS,
		);
		return () => window.clearTimeout(timer);
	}, [focusedKey, focusedRevision]);

	// Nothing appended, nothing rendered — no heading, no prompt. Most citations
	// now open their document directly (DOP-404) and never append a card, so an
	// answer can be fully cited and leave this empty; a standing "click a cited
	// phrase" prompt beside it would be an instruction the reader has already
	// followed. The section earns its space only once it holds something.
	if (chain.length === 0) return null;

	return (
		<section className="citation-chain" data-testid="citation-chain">
			<div className="citation-chain-heading">
				Citations
				<span className="citation-chain-count">
					{chain.length} {chain.length === 1 ? "source" : "sources"}
				</span>
			</div>
			<ol className="citation-chain-list">
				{chain.map((entry, index) => (
					<li
						key={entry.key}
						ref={setItemRef(entry.key)}
						className="citation-chain-item"
						data-citation-key={entry.key}
						data-citation-position={index + 1}
					>
						<span className="citation-chain-ordinal" aria-hidden="true">
							{index + 1}
						</span>
						<div className="citation-chain-card">
							{entry.event ? (
								renderEvidenceEvent({ event: entry.event, allEvents, chatId })
							) : (
								<UnresolvedCard entry={entry} />
							)}
						</div>
						<button
							type="button"
							className="citation-chain-dismiss"
							onClick={() => onDismiss(entry.key)}
							title="Remove this citation"
							aria-label="Remove this citation"
						>
							<X className="citation-chain-dismiss-icon" />
						</button>
					</li>
				))}
			</ol>
		</section>
	);
};

export default CitationChainPanel;
